import { roundTo } from '../../../../utils/geometry/epsilon';
/**
 * Flexible MaxRects Packer với tính năng spacing giữa các part
 */

// ===== CLASS DEFINITIONS =====

class FreeRectangle {
  constructor(x, y, width, height) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
    this.circumference = (width + height) * 2;
    this.area = width * height;
  }
}

class ItemRetangle {
  constructor({
    id,
    width,
    height,
    name,
    quantity = 1,
    thickness,
    material,
    beveled,
    canRotate = false, // **MỚI: Cho phép xoay hay không**
  }) {
    this.id = id;
    this.name = name;
    this.width = width;
    this.height = height;
    this.quantity = quantity;
    this.rotated = false;
    this.x = 0;
    this.y = 0;
    this.binIndex = -1;
    this.thickness = thickness;
    this.material = material;
    this.beveled = beveled;
    this.canRotate = canRotate; // **MỚI**
    this.circumference = (width + height) * 2;

    // Lưu kích thước gốc
    this._originalWidth = width;
    this._originalHeight = height;
  }

  rotate() {
    [this.width, this.height] = [this.height, this.width];
    this.rotated = !this.rotated;
    this.circumference = (this.width + this.height) * 2;
  }

  clone() {
    const item = new ItemRetangle({
      id: this.id,
      width: this._originalWidth,
      height: this._originalHeight,
      name: this.name,
      quantity: 1,
      thickness: this.thickness,
      material: this.material,
      beveled: this.beveled,
      canRotate: this.canRotate, // **MỚI: Clone cả thuộc tính canRotate**
    });

    // Giữ nguyên trạng thái xoay nếu cần
    if (this.rotated) {
      item.rotate();
    }

    return item;
  }
}

class Bin {
  constructor(width, height, index, edgeSpacing = 0) {
    this.width = width;
    this.height = height;
    this.index = index;
    this.items = [];
    this.edgeSpacing = edgeSpacing;

    // Tạo free rectangle ban đầu với edgeSpacing
    const availableWidth = width - edgeSpacing * 2;
    const availableHeight = height - edgeSpacing * 2;
    this.freeRectangles = [
      new FreeRectangle(
        edgeSpacing,
        edgeSpacing,
        availableWidth,
        availableHeight
      ),
    ];
  }

  get usedArea() {
    return this.items.reduce((sum, item) => sum + item.width * item.height, 0);
  }

  get efficiency() {
    const totalArea = this.width * this.height;
    return totalArea > 0 ? this.usedArea / totalArea : 0;
  }

  get remainingArea() {
    return this.width * this.height - this.usedArea;
  }

  // **CẢI TIẾN: Thử đặt item vào bin (có tính canRotate)**
  placeItem(item, spacing = 0, allowRotation = true, strategy = 'BSSF') {
    const placement = this.findBestPosition(
      item,
      spacing,
      allowRotation,
      strategy
    );

    if (placement) {
      item.x = placement.x;
      item.y = placement.y;

      // Chỉ xoay nếu được phép
      if (placement.rotated && item.canRotate && allowRotation) {
        item.rotate();
        item.rotated = true;
      }

      this.items.push(item);
      this.splitFreeRectangles(item, spacing);
      return true;
    }

    return false;
  }

  // **CẢI TIẾN: Tìm vị trí tốt nhất (tôn trọng canRotate)**
  findBestPosition(item, spacing, allowRotation, strategy = 'BSSF') {
    let bestScore = Infinity;
    let bestPosition = null;

    // Xác định số lượng hướng thử (1 hoặc 2)
    const rotationAttempts = allowRotation && item.canRotate ? 2 : 1;

    for (let rotated = 0; rotated < rotationAttempts; rotated++) {
      const w = (rotated ? item.height : item.width) + spacing;
      const h = (rotated ? item.width : item.height) + spacing;

      for (const rect of this.freeRectangles) {
        if (w <= rect.width && h <= rect.height) {
          const leftoverX = rect.width - w;
          const leftoverY = rect.height - h;
          const shortSide = Math.min(leftoverX, leftoverY);
          const longSide = Math.max(leftoverX, leftoverY);
          const wastedArea = leftoverX * leftoverY;
          const contactPerimeter = this.calculateContactPerimeter(
            rect.x,
            rect.y,
            w,
            h
          );

          // Chọn chiến lược tính điểm
          let score;
          switch (strategy) {
            case 'BSSF':
              // Best Short Side Fit - Fit chặt nhất theo cạnh ngắn
              score = shortSide * 1000 + longSide;
              break;

            case 'BLSF':
              // Best Long Side Fit - Fit chặt nhất theo cạnh dài
              score = longSide * 1000 + shortSide;
              break;

            case 'BAF':
              // Best Area Fit - Giảm thiểu diện tích lãng phí
              score = wastedArea;
              break;

            case 'BSSF_BAF':
              // Hybrid: Ưu tiên shortSide, phụ wasted area
              score = shortSide * 10000 + wastedArea;
              break;

            case 'BL':
              // Bottom-Left - Ưu tiên góc dưới trái
              score = rect.y * 10000 + rect.x;
              break;

            case 'CP':
              // Contact Perimeter - Ưu tiên vị trí tiếp xúc nhiều với items khác
              score = -contactPerimeter * 1000 + shortSide;
              break;

            case 'HYBRID':
              // Hybrid nâng cao: Cân bằng nhiều yếu tố
              score =
                (shortSide * 0.4 + longSide * 0.2) * 1000 +
                wastedArea * 0.3 -
                contactPerimeter * 0.1;
              break;

            case 'COMPACT':
              // Compact - Tạo khối chặt, ưu tiên bottom-left + short side
              score = rect.y * 5000 + rect.x * 100 + shortSide * 10;
              break;

            default:
              score = shortSide * 1000 + longSide;
          }

          if (score < bestScore) {
            bestScore = score;
            bestPosition = {
              x: rect.x,
              y: rect.y,
              rotated: rotated === 1,
              rect: rect,
              score: score,
              metrics: { shortSide, longSide, wastedArea, contactPerimeter },
            };
          }
        }
      }
    }

    return bestPosition;
  }

  // Tính chu vi tiếp xúc với items khác và biên
  calculateContactPerimeter(x, y, width, height) {
    let contact = 0;
    const edges = {
      left: x,
      right: x + width,
      top: y + height,
      bottom: y,
    };

    // Tiếp xúc với biên bin
    if (edges.left === this.edgeSpacing) contact += height;
    if (edges.bottom === this.edgeSpacing) contact += width;
    if (edges.right === this.width - this.edgeSpacing) contact += height;
    if (edges.top === this.height - this.edgeSpacing) contact += width;

    // Tiếp xúc với items khác
    for (const item of this.items) {
      // Tiếp xúc cạnh trái
      if (
        edges.left === item.x + item.width &&
        !(edges.top <= item.y || edges.bottom >= item.y + item.height)
      ) {
        contact +=
          Math.min(edges.top, item.y + item.height) -
          Math.max(edges.bottom, item.y);
      }
      // Tiếp xúc cạnh phải
      if (
        edges.right === item.x &&
        !(edges.top <= item.y || edges.bottom >= item.y + item.height)
      ) {
        contact +=
          Math.min(edges.top, item.y + item.height) -
          Math.max(edges.bottom, item.y);
      }
      // Tiếp xúc cạnh dưới
      if (
        edges.bottom === item.y + item.height &&
        !(edges.right <= item.x || edges.left >= item.x + item.width)
      ) {
        contact +=
          Math.min(edges.right, item.x + item.width) -
          Math.max(edges.left, item.x);
      }
      // Tiếp xúc cạnh trên
      if (
        edges.top === item.y &&
        !(edges.right <= item.x || edges.left >= item.x + item.width)
      ) {
        contact +=
          Math.min(edges.right, item.x + item.width) -
          Math.max(edges.left, item.x);
      }
    }

    return contact;
  }

  // Chia nhỏ free rectangles sau khi đặt item (có tính spacing)
  splitFreeRectangles(item, spacing) {
    const newRects = [];
    const itemWithSpacing = {
      x: item.x,
      y: item.y,
      width: item.width + spacing,
      height: item.height + spacing,
    };

    for (const rect of this.freeRectangles) {
      if (this.intersects(rect, itemWithSpacing)) {
        // Rectangle bên phải
        if (itemWithSpacing.x + itemWithSpacing.width < rect.x + rect.width) {
          newRects.push(
            new FreeRectangle(
              itemWithSpacing.x + itemWithSpacing.width,
              rect.y,
              rect.x + rect.width - (itemWithSpacing.x + itemWithSpacing.width),
              rect.height
            )
          );
        }

        // Rectangle phía trên
        if (itemWithSpacing.y + itemWithSpacing.height < rect.y + rect.height) {
          newRects.push(
            new FreeRectangle(
              rect.x,
              itemWithSpacing.y + itemWithSpacing.height,
              rect.width,
              rect.y +
                rect.height -
                (itemWithSpacing.y + itemWithSpacing.height)
            )
          );
        }

        // Rectangle bên trái
        if (rect.x < itemWithSpacing.x) {
          newRects.push(
            new FreeRectangle(
              rect.x,
              rect.y,
              itemWithSpacing.x - rect.x,
              rect.height
            )
          );
        }

        // Rectangle phía dưới
        if (rect.y < itemWithSpacing.y) {
          newRects.push(
            new FreeRectangle(
              rect.x,
              rect.y,
              rect.width,
              itemWithSpacing.y - rect.y
            )
          );
        }
      } else {
        newRects.push(rect);
      }
    }

    this.freeRectangles = this.pruneFreeRectangles(newRects);
  }

  intersects(rect, item) {
    return !(
      item.x >= rect.x + rect.width ||
      item.x + item.width <= rect.x ||
      item.y >= rect.y + rect.height ||
      item.y + item.height <= rect.y
    );
  }

  pruneFreeRectangles(rects) {
    const result = [];

    for (let i = 0; i < rects.length; i++) {
      const rect1 = rects[i];
      let isContained = false;

      for (let j = 0; j < rects.length; j++) {
        if (i === j) continue;
        const rect2 = rects[j];

        if (this.isRectangleContained(rect1, rect2)) {
          isContained = true;
          break;
        }
      }

      if (!isContained) {
        result.push(rect1);
      }
    }

    return result;
  }

  isRectangleContained(rect1, rect2) {
    return (
      rect1.x >= rect2.x &&
      rect1.y >= rect2.y &&
      rect1.x + rect1.width <= rect2.x + rect2.width &&
      rect1.y + rect1.height <= rect2.y + rect2.height
    );
  }
}

// ===== NESTING ALGORITHM =====

class NestingAlgorithm {
  constructor(binWidth, binHeight, options = {}) {
    this.binWidth = binWidth;
    this.binHeight = binHeight;

    // **CẢI TIẾN: Options đầy đủ hơn**
    this.spacing = options.spacing || 0;
    this.edgeSpacing = options.edgeSpacing || 0;
    this.allowRotation = options.allowRotation === true;
    this.strategy = options.strategy || 'CP'; // **MỚI: Chiến lược tính điểm**

    this.bins = [];
    this.inputItems = [];
    this.unpackedItems = [];
    this.packedCount = 0;
  }

  // Thực hiện nesting
  nestItems(items) {
    this.inputItems = [...items];

    // Mở rộng items theo quantity
    const expandedItems = [];
    items.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        const newItem = item.clone();
        newItem.id = `${item.id}_${i + 1}`;
        expandedItems.push(newItem);
      }
    });

    // Sắp xếp items theo diện tích giảm dần
    expandedItems.sort((a, b) => b.width * b.height - a.width * a.height);

    // Đặt từng item
    for (const item of expandedItems) {
      let placed = false;

      // Thử đặt vào các bin hiện có
      for (const bin of this.bins) {
        if (
          bin.placeItem(item, this.spacing, this.allowRotation, this.strategy)
        ) {
          item.binIndex = bin.index;
          placed = true;
          this.packedCount++;
          break;
        }
      }

      // Nếu không đặt được, tạo bin mới
      if (!placed) {
        const newBin = new Bin(
          this.binWidth,
          this.binHeight,
          this.bins.length,
          this.edgeSpacing
        );

        if (
          newBin.placeItem(
            item,
            this.spacing,
            this.allowRotation,
            this.strategy
          )
        ) {
          item.binIndex = newBin.index;
          this.bins.push(newBin);
          this.packedCount++;
        } else {
          // Item quá lớn, không xếp được
          this.unpackedItems.push(item);
          console.error(`Cannot place item ${item.id} - too large for bin!`);
        }
      }
    }

    return this.bins;
  }

  getOverallEfficiency() {
    if (this.bins.length === 0) return 0;

    const totalUsedArea = this.bins.reduce((sum, bin) => sum + bin.usedArea, 0);
    const totalArea = this.bins.length * this.binWidth * this.binHeight;

    return Math.round((totalUsedArea / totalArea) * 100 * 100) / 100;
  }

  getPackingResult() {
    return {
      binSize: {
        width: this.binWidth,
        height: this.binHeight,
      },
      totalBins: this.bins.length,
      totalItems: this.inputItems.length,
      packedItems: this.packedCount,
      unpackedItems: this.unpackedItems.length,
      spacing: this.spacing,
      edgeSpacing: this.edgeSpacing,
      allowRotation: this.allowRotation, // **MỚI**
      bins: this.bins.map(bin => ({
        index: bin.index,
        width: bin.width,
        height: bin.height,
        items: bin.items.map(item => {
          const result = { ...item };
          Object.keys(result).forEach(key => {
            if (key.startsWith('_')) {
              delete result[key];
            }
          });
          return result;
        }),
        usedArea: bin.usedArea,
        totalArea: bin.width * bin.height,
        efficiency: Math.round(bin.efficiency * 100 * 100) / 100,
      })),
      unpackedItems: this.unpackedItems.map(item => {
        const result = { ...item };
        Object.keys(result).forEach(key => {
          if (key.startsWith('_')) {
            delete result[key];
          }
        });
        result.reason = 'Item too large for bin size';
        return result;
      }),
      circumference: this.bins.reduce(
        (prev, curr) =>
          prev + curr.items.reduce((a, b) => a + b.circumference, 0),
        0
      ),
      overallEfficiency: this.getOverallEfficiency(),
    };
  }

  visualize(containerId, options = {}, title) {
    const result = this.getPackingResult();
    return visualizePackingResult(result, containerId, options, title);
  }
}

class MaxRectsPacker {
  constructor(binWidth, binHeight, options = {}) {
    this.binWidth = binWidth;
    this.binHeight = binHeight;

    // Cấu hình thuật toán
    this.allowRotation = options.allowRotation !== false;
    this.sortMethod = options.sortMethod || 'AREA_DESC';
    this.packingMethod = options.packingMethod || 'BEST_SHORT_SIDE_FIT';

    // **MỚI: Thêm spacing**
    this.spacing = options.spacing || 0; // Khoảng cách giữa các item (mặc định = 0)
    this.edgeSpacing =
      options.edgeSpacing !== undefined ? options.edgeSpacing : 0; // Khoảng cách từ biên

    this.inputItems = [];
    this.bins = [];
    this.packedCount = 0;
    this.unpackedItems = [];
  }

  /**
   * Giữ nguyên orientation gốc của item
   */
  normalizeItemOrientation(item) {
    return {
      width: item.width,
      height: item.height,
      needsRotation: false,
    };
  }

  /**
   * Thêm item vào danh sách cần pack
   */
  addItem(item, quantity = 1) {
    if (
      !item ||
      typeof item.width !== 'number' ||
      typeof item.height !== 'number'
    ) {
      throw new Error('Item must have numeric width and height properties');
    }

    // FIXED [I7]: Add NaN validation
    if (isNaN(item.width) || isNaN(item.height)) {
      throw new Error(`Item has NaN dimensions: width=${item.width}, height=${item.height}, id=${item.id}`);
    }

    if (item.width <= 0 || item.height <= 0) {
      throw new Error('Item width and height must be positive numbers');
    }

    for (let i = 0; i < quantity; i++) {
      const itemCopy = {
        ...item,
        _originalWidth: item.width,
        _originalHeight: item.height,
        _rotated: false,
        _placed: false,
        _binIndex: -1,
        _x: 0,
        _y: 0,
        _index: this.inputItems.length,
        circumference: (item.width + item.height) * 2,
      };

      if (quantity > 1 && item.id !== undefined) {
        itemCopy.id = `${item.id}_${i + 1}`;
      }

      this.inputItems.push(itemCopy);
    }
  }

  addItems(items) {
    items.forEach(item => {
      const quantity = item.quantity || 1;
      const itemData = { ...item };
      delete itemData.quantity;
      this.addItem(itemData, quantity);
    });
  }

  sortItems() {
    switch (this.sortMethod) {
      case 'AREA_DESC':
        this.inputItems.sort((a, b) => b.width * b.height - a.width * a.height);
        break;
      case 'PERIMETER_DESC':
        this.inputItems.sort(
          (a, b) => 2 * (b.width + b.height) - 2 * (a.width + a.height)
        );
        break;
      case 'WIDTH_DESC':
        this.inputItems.sort((a, b) => b.width - a.width);
        break;
      case 'HEIGHT_DESC':
        this.inputItems.sort((a, b) => b.height - a.height);
        break;
      case 'RATIO_DESC':
        this.inputItems.sort((a, b) => {
          const ratioA =
            Math.max(a.width, a.height) / Math.min(a.width, a.height);
          const ratioB =
            Math.max(b.width, b.height) / Math.min(b.width, b.height);
          return ratioB - ratioA;
        });
        break;
      case 'LONGEST_SIDE_DESC':
        this.inputItems.sort(
          (a, b) => Math.max(b.width, b.height) - Math.max(a.width, a.height)
        );
        break;
    }
  }

  findBestPosition(bin, item) {
    let bestPosition = null;
    let bestScore = Number.MAX_VALUE;

    const orientations = [
      { width: item.width, height: item.height, rotated: false },
    ];

    if (this.allowRotation && item.canRotate && item.width !== item.height) {
      orientations.push({
        width: item.height,
        height: item.width,
        rotated: true,
      });
    }

    for (const orientation of orientations) {
      // **MỚI: Thêm spacing vào kích thước cần kiểm tra**
      const requiredWidth =
        orientation.width === 2440 ? 2440 : orientation.width + this.spacing;
      const requiredHeight =
        orientation.height === 1220 ? 1220 : orientation.height + this.spacing;

      for (const freeRect of bin.freeRectangles) {
        if (
          freeRect.width >= requiredWidth &&
          freeRect.height >= requiredHeight
        ) {
          const leftoverHoriz = freeRect.width - requiredWidth;
          const leftoverVert = freeRect.height - requiredHeight;

          let score;
          switch (this.packingMethod) {
            case 'BEST_SHORT_SIDE_FIT':
              score = Math.min(leftoverHoriz, leftoverVert);
              break;
            case 'BEST_LONG_SIDE_FIT':
              score = Math.max(leftoverHoriz, leftoverVert);
              break;
            case 'BEST_AREA_FIT':
              score = leftoverHoriz * leftoverVert;
              break;
            default:
              score = Math.min(leftoverHoriz, leftoverVert);
          }

          if (score < bestScore) {
            bestScore = score;
            bestPosition = {
              x: freeRect.x,
              y: freeRect.y,
              width: orientation.width,
              height: orientation.height,
              rotated: orientation.rotated,
              score: score,
            };
          }
        }
      }
    }

    return bestPosition;
  }

  placeItem(bin, item, position) {
    // **MỚI: Áp dụng edge spacing cho item đầu tiên**
    const actualX = position.x === 0 ? this.edgeSpacing : position.x;
    const actualY = position.y === 0 ? this.edgeSpacing : position.y;

    item._x = actualX;
    item._y = actualY;
    item._binIndex = bin.index;
    item._placed = true;

    if (position.rotated) {
      [item.width, item.height] = [item.height, item.width];
      item._rotated = true;
    }

    const itemData = {
      ...item,
      x: actualX,
      y: actualY,
      width: position.width,
      height: position.height,
      rotated: position.rotated,
    };

    bin.items.push(itemData);

    // **MỚI: Update free rectangles với spacing**
    const placedRectWithSpacing = {
      x: position.x,
      y: position.y,
      width: position.width + this.spacing,
      height: position.height + this.spacing,
    };

    this.updateFreeRectangles(bin, placedRectWithSpacing);
  }

  updateFreeRectangles(bin, placedRect) {
    const newFreeRects = [];

    for (const freeRect of bin.freeRectangles) {
      if (this.intersects(freeRect, placedRect)) {
        const splits = this.splitFreeRectangle(freeRect, placedRect);
        newFreeRects.push(...splits);
      } else {
        newFreeRects.push(freeRect);
      }
    }

    bin.freeRectangles = this.pruneFreeList(newFreeRects);
  }

  intersects(rect1, rect2) {
    return (
      rect1.x < rect2.x + rect2.width &&
      rect1.x + rect1.width > rect2.x &&
      rect1.y < rect2.y + rect2.height &&
      rect1.y + rect1.height > rect2.y
    );
  }

  splitFreeRectangle(freeRect, usedRect) {
    const result = [];

    if (usedRect.x > freeRect.x) {
      result.push(
        new FreeRectangle(
          freeRect.x,
          freeRect.y,
          usedRect.x - freeRect.x,
          freeRect.height
        )
      );
    }

    if (usedRect.x + usedRect.width < freeRect.x + freeRect.width) {
      result.push(
        new FreeRectangle(
          usedRect.x + usedRect.width,
          freeRect.y,
          freeRect.x + freeRect.width - (usedRect.x + usedRect.width),
          freeRect.height
        )
      );
    }

    if (usedRect.y > freeRect.y) {
      result.push(
        new FreeRectangle(
          freeRect.x,
          freeRect.y,
          freeRect.width,
          usedRect.y - freeRect.y
        )
      );
    }

    if (usedRect.y + usedRect.height < freeRect.y + freeRect.height) {
      result.push(
        new FreeRectangle(
          freeRect.x,
          usedRect.y + usedRect.height,
          freeRect.width,
          freeRect.y + freeRect.height - (usedRect.y + usedRect.height)
        )
      );
    }

    return result.filter(rect => rect.width > 0 && rect.height > 0);
  }

  pruneFreeList(freeRects) {
    const pruned = [];

    for (let i = 0; i < freeRects.length; i++) {
      let contained = false;

      for (let j = 0; j < freeRects.length; j++) {
        if (i !== j && this.isContainedIn(freeRects[i], freeRects[j])) {
          contained = true;
          break;
        }
      }

      if (!contained) {
        pruned.push(freeRects[i]);
      }
    }

    return pruned;
  }

  isContainedIn(rect1, rect2) {
    return (
      rect2.x <= rect1.x &&
      rect2.y <= rect1.y &&
      rect2.x + rect2.width >= rect1.x + rect1.width &&
      rect2.y + rect2.height >= rect1.y + rect1.height
    );
  }

  createNewBin() {
    const bin = new Bin(this.binWidth, this.binHeight, this.bins.length);

    // **MỚI: Nếu có edgeSpacing, tạo free rectangle nhỏ hơn**
    if (this.edgeSpacing > 0) {
      bin.freeRectangles = [
        new FreeRectangle(
          this.edgeSpacing,
          this.edgeSpacing,
          this.binWidth - 2 * this.edgeSpacing,
          this.binHeight - 2 * this.edgeSpacing
        ),
      ];
    }

    this.bins.push(bin);
    return bin;
  }

  findBestBin(item) {
    let bestBin = null;
    let bestPosition = null;
    let bestScore = Number.MAX_VALUE;

    for (const bin of this.bins) {
      const position = this.findBestPosition(bin, item);
      if (position && position.score < bestScore) {
        bestBin = bin;
        bestPosition = position;
        bestScore = position.score;
      }
    }

    return bestBin ? { bin: bestBin, position: bestPosition } : null;
  }

  pack() {
    if (this.inputItems.length === 0) {
      return { success: true, message: 'No items to pack' };
    }

    this.bins = [];
    this.packedCount = 0;
    this.unpackedItems = [];

    this.sortItems();

    for (const item of this.inputItems) {
      let placed = false;

      const bestFit = this.findBestBin(item);
      if (bestFit) {
        this.placeItem(bestFit.bin, item, bestFit.position);
        placed = true;
        this.packedCount++;
      } else {
        const newBin = this.createNewBin();
        const position = this.findBestPosition(newBin, item);

        if (position) {
          this.placeItem(newBin, item, position);
          placed = true;
          this.packedCount++;
        } else {
          this.unpackedItems.push(item);
        }
      }
    }

    return {
      success: this.unpackedItems.length === 0,
      packedCount: this.packedCount,
      unpackedCount: this.unpackedItems.length,
      binsUsed: this.bins.length,
      message:
        this.unpackedItems.length > 0
          ? `${this.unpackedItems.length} items could not be packed`
          : 'All items packed successfully',
    };
  }

  getPackingResult() {
    return {
      binSize: {
        width: this.binWidth,
        height: this.binHeight,
      },
      totalBins: this.bins.length,
      totalItems: this.inputItems.length,
      packedItems: this.packedCount,
      unpackedItems: this.unpackedItems.length,
      spacing: this.spacing, // **MỚI: Thêm thông tin spacing**
      edgeSpacing: this.edgeSpacing,
      bins: this.bins.map(bin => ({
        index: bin.index,
        width: bin.width,
        height: bin.height,
        items: bin.items.map(item => {
          const result = { ...item };
          Object.keys(result).forEach(key => {
            if (key.startsWith('_')) {
              delete result[key];
            }
          });
          return result;
        }),
        usedArea: bin.usedArea,
        totalArea: bin.width * bin.height,
        efficiency: Math.round(bin.efficiency * 100 * 100) / 100,
      })),
      unpackedItems: this.unpackedItems.map(item => {
        const result = { ...item };
        Object.keys(result).forEach(key => {
          if (key.startsWith('_')) {
            delete result[key];
          }
        });
        result.reason = 'Item too large for bin size';
        return result;
      }),
      circumference: this.bins.reduce(
        (prev, curr) =>
          prev + curr.items.reduce((a, b) => a + b.circumference, 0),
        0
      ),
      overallEfficiency: this.getOverallEfficiency(),
    };
  }

  getOverallEfficiency() {
    if (this.bins.length === 0) return 0;

    const totalUsedArea = this.bins.reduce((sum, bin) => sum + bin.usedArea, 0);
    const totalBinArea = this.bins.length * this.binWidth * this.binHeight;

    return Math.round((totalUsedArea / totalBinArea) * 100 * 100) / 100;
  }

  getStatistics() {
    const result = this.getPackingResult();

    return {
      summary: {
        binsUsed: result.totalBins,
        totalItems: result.totalItems,
        packedItems: result.packedItems,
        unpackedItems: result.unpackedItems,
        overallEfficiency: `${result.overallEfficiency}%`,
        wastePercentage: `${
          Math.round((100 - result.overallEfficiency) * 100) / 100
        }%`,
        spacing: result.spacing, // **MỚI**
        edgeSpacing: result.edgeSpacing, // **MỚI**
      },
      binDetails: result.bins.map(bin => ({
        binIndex: bin.index,
        itemCount: bin.items.length,
        efficiency: `${bin.efficiency}%`,
        usedArea: bin.usedArea,
        wasteArea: bin.totalArea - bin.usedArea,
      })),
      settings: {
        binSize: `${this.binWidth} x ${this.binHeight}`,
        allowRotation: this.allowRotation,
        sortMethod: this.sortMethod,
        packingMethod: this.packingMethod,
        spacing: this.spacing, // **MỚI**
        edgeSpacing: this.edgeSpacing, // **MỚI**
      },
    };
  }

  visualize(containerId, options = {}, title) {
    const result = this.getPackingResult();
    return visualizePackingResult(result, containerId, options, title);
  }
}

// === VISUALIZATION FUNCTIONS ===

/**
 * Vẽ indicator cho chiều dài của part
 */
function drawLengthIndicator(ctx, item, x, y, width, height) {
  ctx.save();
  ctx.strokeStyle = '#2ed573'; // màu xanh
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 3]);

  const centerX = x + width / 2;
  const centerY = y + height / 2;

  // Xác định chiều dài (cạnh lớn hơn)
  const isHorizontal = height;
  const length = Math.min(width, height) * 0.7;

  if (isHorizontal) {
    // Vẽ đường ngang (chiều dài theo chiều ngang)
    ctx.beginPath();
    ctx.moveTo(centerX - length / 2, centerY);
    ctx.lineTo(centerX + length / 2, centerY);
    ctx.stroke();

    // Mũi tên hai đầu
    ctx.setLineDash([]);
    // Mũi tên trái
    ctx.beginPath();
    ctx.moveTo(centerX - length / 2 + 5, centerY - 3);
    ctx.lineTo(centerX - length / 2, centerY);
    ctx.lineTo(centerX - length / 2 + 5, centerY + 3);
    ctx.stroke();
    // Mũi tên phải
    ctx.beginPath();
    ctx.moveTo(centerX + length / 2 - 5, centerY - 3);
    ctx.lineTo(centerX + length / 2, centerY);
    ctx.lineTo(centerX + length / 2 - 5, centerY + 3);
    ctx.stroke();
  } else {
    // Vẽ đường dọc (chiều dài theo chiều dọc)
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - length / 2);
    ctx.lineTo(centerX, centerY + length / 2);
    ctx.stroke();

    // Mũi tên hai đầu
    ctx.setLineDash([]);
    // Mũi tên trên
    ctx.beginPath();
    ctx.moveTo(centerX - 3, centerY - length / 2 + 5);
    ctx.lineTo(centerX, centerY - length / 2);
    ctx.lineTo(centerX + 3, centerY - length / 2 + 5);
    ctx.stroke();
    // Mũi tên dưới
    ctx.beginPath();
    ctx.moveTo(centerX - 3, centerY + length / 2 - 5);
    ctx.lineTo(centerX, centerY + length / 2);
    ctx.lineTo(centerX + 3, centerY + length / 2 - 5);
    ctx.stroke();
  }

  ctx.restore();
}

// === VISUALIZATION FUNCTIONS ===

// Màu sắc cho items
const VISUALIZATION_COLORS = [
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#96CEB4',
  '#FFEAA7',
  '#DDA0DD',
  '#98D8C8',
  '#F7DC6F',
  '#BB8FCE',
  '#85C1E9',
  '#F8C471',
  '#82E0AA',
  '#F1948A',
  '#85C1E9',
  '#F8D7DA',
  '#D5DBDB',
  '#AED6F1',
  '#A9DFBF',
  '#F9E79F',
  '#D7BDE2',
];

function clearVisual(containerId) {
  const containerAll = document.getElementById(containerId);
  if (containerAll) {
    containerAll.innerHTML = '';
  }
}

function visualizeTotal(
  total,
  containerId,
  materialAndLaborPrices,
  extendedPrices,
  discountedPlankPrice
) {
  const containerAll = document.getElementById(containerId);
  if (!containerAll) {
    console.error(`Container with id "${containerId}" not found`);
    return null;
  }

  const container = document.createElement('div');
  containerAll.appendChild(container);

  const {
    total: totalPrice,
    originTotal,
    shippingCharge,
    totalMaterials,
    totalLabors,
    totalOthers,
    profitMargin,
    totalFrames,
    totalMaterialAndLabor,
  } = total;

  const materialLaborOriginPrices = [];
  const extendedOriginPrices = [];
  const materialLaborAfterPrices = [];
  const extendedAfterPrices = [];
  let materialLaborOriginTotal = 0;
  let extendedOriginTotal = 0;
  materialAndLaborPrices.forEach(item => {
    const { name, total, formular, note } = item;
    const origin = formular?.reduce((prev, curr) => {
      return prev + roundTo(curr.param2?.price * curr.param1, 0);
    }, 0);
    if (!origin || !total) return;
    materialLaborOriginPrices.push({ name, origin, note });
    materialLaborAfterPrices.push({ name, after: total, note });
  });
  extendedPrices.forEach(item => {
    const { name, total, formular, note } = item;
    const origin = formular?.reduce((prev, curr) => {
      return prev + roundTo(curr.param2?.price * curr.param1, 0);
    }, 0);
    if (!origin || !total) return;
    extendedOriginPrices.push({ name, origin, note });
    extendedAfterPrices.push({ name, after: total, note });
  });
  materialLaborOriginTotal = materialLaborOriginPrices.reduce((prev, curr) => {
    return prev + curr.origin;
  }, 0);
  extendedOriginTotal = extendedOriginPrices.reduce((prev, curr) => {
    return prev + curr.origin;
  }, 0);

  const render = (title, total, expand) => {
    return `<span class="text-body-medium" style="margin-top:40px">${title}<p style="margin-top:20px"></p>${expand}</span></span>`;
  };

  const priceConfigRows = [
    ...(materialAndLaborPrices
      ?.filter(item => item.total)
      ?.map(item => {
        return {
          name: item.name,
          note: item.note,
          origin:
            item.formular
              ?.map(item => {
                if (!item.param1 || !item.param2?.price) return;
                return `<div class="text-body-small">${
                  item.note ? `(${item.note})` : ''
                }<span class="text-label-large">${roundTo(item.param1, 3)} ${
                  item.param2?.unit ?? ''
                }  *  ${roundTo(item.param2?.price, 0).toLocaleString('vi-VN', {
                  currency: 'VND',
                  style: 'currency',
                })} </span><span class="text-body-small text-black-low"> =  ${roundTo(
                  item.param2?.price * item.param1,
                  0
                ).toLocaleString('vi-VN', {
                  currency: 'VND',
                  style: 'currency',
                })}</span></div>`;
              })
              ?.filter(item => item)
              ?.join(' + ') +
            ` = ${roundTo(
              item.formular?.reduce((a, b) => {
                return a + (b?.param2?.price ?? 0) * b.param1;
              }, 0),
              0
            ).toLocaleString('vi-VN', {
              currency: 'VND',
              style: 'currency',
            })} `,
          after: `(${roundTo(
            item.formular?.reduce((a, b) => {
              return a + (b?.param2?.price ?? 0) * b.param1;
            }, 0),
            0
          ).toLocaleString('vi-VN', {
            currency: 'VND',
            style: 'currency',
          })} / ${profitMargin}) * 1.08 <span class="text-label-large"> = ${roundTo(item.total, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}</span>`,
        };
      }) ?? []),
    ...(extendedPrices
      ?.filter(item => item.total)
      ?.map((item, index) => ({
        name: item.name,
        origin:
          item.formular
            ?.map(item => {
              if (!item.param1 || !item.param2?.price) return;
              return `<div class="text-body-small">${
                item.note ? `(${item.note})` : ''
              }<span class="text-label-large">${roundTo(item.param1, 3)} ${
                item.param2?.unit ?? ''
              }  *  ${roundTo(item.param2?.price, 0).toLocaleString('vi-VN', {
                currency: 'VND',
                style: 'currency',
              })} </span><span class="text-label-large"> =  ${roundTo(
                item.param2?.price * item.param1,
                0
              ).toLocaleString('vi-VN', {
                currency: 'VND',
                style: 'currency',
              })}</span></div>`;
            })
            ?.filter(item => item)
            ?.join(' + ') +
          ` = ${roundTo(
            item.formular?.reduce((a, b) => {
              return a + b.param2?.price * b.param1;
            }, 0),
            0
          ).toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })} `,
        after: `<span class="text-label-large"> = ${roundTo(item.total, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}</span>`,
      })) ?? []),
    {
      name: 'Tổng giá phần gỗ',
      origin:
        materialLaborOriginPrices
          ?.map(
            item =>
              `<span class="text-body-medium">${roundTo(
                item.origin,
                0
              ).toLocaleString('vi-VN')}</span>`
          )
          ?.join(' + ') +
        ` <span class="text-label-large"> = ${roundTo(materialLaborOriginTotal, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}</span>`,
      after: `(${roundTo(materialLaborOriginTotal, 0).toLocaleString('vi-VN')} / ${profitMargin}) * 1.08 <span class="text-label-large"> = ${roundTo(totalMaterialAndLabor, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}</span>`,
    },
    {
      name: 'Tổng giá phụ kiện, mặt bếp và vách bếp',
      origin:
        extendedOriginPrices
          ?.map(
            item =>
              `<span class="text-body-medium">${roundTo(
                item.origin,
                0
              ).toLocaleString('vi-VN')}</span>`
          )
          ?.join(' + ') +
        ` <span class="text-label-large"> = ${roundTo(extendedOriginTotal, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}</span>`,
      after: `<span class="text-label-large"> = ${roundTo(extendedOriginTotal, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}</span>`,
    },
    {
      name: 'Tổng tiền',
      origin:
        `<span class="text-body-medium">${roundTo(
          materialLaborOriginTotal,
          0
        ).toLocaleString('vi-VN')}</span>` +
        ` + <span class="text-body-medium">${roundTo(
          extendedOriginTotal,
          0
        ).toLocaleString('vi-VN')}</span>` +
        `<span class="text-body-medium !font-bold"> = ${roundTo(materialLaborOriginTotal + extendedOriginTotal, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}</span>`,
      after:
        `<span class="text-body-medium">${roundTo(
          totalMaterialAndLabor,
          0
        ).toLocaleString('vi-VN')}</span>` +
        ` + <span class="text-body-medium">${roundTo(
          extendedOriginTotal,
          0
        ).toLocaleString('vi-VN')}</span>` +
        `<span class="text-body-medium !font-bold"> = ${roundTo(totalPrice, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}</span>`,
      note: discountedPlankPrice
        ? `<span class="text-body-medium">${roundTo(
            totalPrice,
            0
          ).toLocaleString('vi-VN')}</span>` +
          ` - <span class="text-body-medium">
              ${roundTo(discountedPlankPrice, 0)?.toLocaleString('vi-VN')}
            </span>` +
          `<span class="text-body-medium !font-bold"> = ${roundTo(totalPrice - discountedPlankPrice, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}</span>`
        : '',
    },
  ];

  const renderTableRows = rows =>
    rows
      .map(
        ({ name, origin, after, note = '' }, index) => `
          <tr>
            <td style="padding:12px 14px;border:1px solid #d9e2ec;vertical-align:top;">${index}</td>
            <td style="padding:12px 14px;border:1px solid #d9e2ec;vertical-align:top;">${name}</td>
            <td style="padding:12px 14px;border:1px solid #d9e2ec;vertical-align:top;font-weight:600;white-space:wrap;">${origin}</td>
            <td style="padding:12px 14px;border:1px solid #d9e2ec;vertical-align:top;">${after}</td>
            <td style="padding:12px 14px;border:1px solid #d9e2ec;vertical-align:top;">${note}</td>
          </tr>
        `
      )
      .join('');

  container.innerHTML = `<div data-price-table-wrapper style="display:block;margin-top:12px;border:1px solid #d9e2ec;border-radius:16px;overflow:hidden;background:#fff;box-shadow:0 12px 30px rgba(15, 23, 42, 0.08);">
        <div style="padding:16px 20px;background:linear-gradient(135deg,#f8fafc 0%,#e2e8f0 100%);border-bottom:1px solid #d9e2ec;">
          <div style="font-size:18px;font-weight:700;color:#0f172a;">Bảng cấu hình đơn giá</div>
          <div style="margin-top:4px;font-size:13px;color:#475569;">Hiển thị toàn bộ thông số đơn giá đang dùng trong BaseNesting.</div>
        </div>
        <div style="overflow:auto;">
          <table style="width:100%;border-collapse:collapse;min-width:920px;background:#fff;">
            <thead>
              <tr style="background:#f8fafc;color:#0f172a;">
                <th style="padding:12px 14px;border:1px solid #d9e2ec;text-align:left;">Số TT</th>
                <th style="padding:12px 14px;border:1px solid #d9e2ec;text-align:left;">Tên</th>
                <th style="padding:12px 14px;border:1px solid #d9e2ec;text-align:left;">Giá vốn</th>
                <th style="padding:12px 14px;border:1px solid #d9e2ec;text-align:left;">Giá bán (Đã thêm TSLN nếu có)</th>
                <th style="padding:12px 14px;border:1px solid #d9e2ec;text-align:left;">Ghi chú</th>
              </tr>
            </thead>
            <tbody>
              ${renderTableRows(priceConfigRows)}
            </tbody>
          </table>
        </div>
      </div>`;

  return [
    ...(materialAndLaborPrices
      ?.filter(item => item.total)
      ?.map(mt => {
        return mt.formular
          ?.filter(item => item.param1)
          ?.map(item => {
            const total = item.param2?.price * item.param1;
            return {
              type: mt.name,
              name: item.note,
              quantity: roundTo(item.param1, 3),
              price: item.param2?.price,
              unit: item.param2?.unit,
              origin: roundTo(total, 0),
              after: roundTo((total / profitMargin) * 1.08, 0),
            };
          });
      })
      ?.flat() ?? []),
    ...(extendedPrices
      ?.filter(item => item.total)
      ?.map(mt => {
        return mt.formular
          ?.filter(item => item.param1)
          ?.map(item => {
            const total = item.param2?.price * item.param1;
            return {
              type: mt.name,
              name: item.note,
              quantity: roundTo(item.param1, 3),
              price: item.param2?.price,
              unit: item.param2?.unit,
              origin: roundTo(total, 0),
              after: roundTo(total, 0),
            };
          });
      })
      ?.flat() ?? []),
    {
      name: 'Tổng giá phần gỗ',
      origin:
        materialLaborOriginPrices
          ?.map(item => `${roundTo(item.origin, 0).toLocaleString('vi-VN')}`)
          ?.join(' + ') +
        `  = ${roundTo(materialLaborOriginTotal, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}`,
      after: `(${roundTo(materialLaborOriginTotal, 0).toLocaleString('vi-VN')} / ${profitMargin}) * 1.08  = ${roundTo(totalMaterialAndLabor, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}`,
    },
    {
      name: 'Tổng giá phụ kiện, mặt bếp và vách bếp',
      origin:
        extendedOriginPrices
          ?.map(item => `${roundTo(item.origin, 0).toLocaleString('vi-VN')}`)
          ?.join(' + ') +
        `  = ${roundTo(extendedOriginTotal, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}`,
      after: ` = ${roundTo(extendedOriginTotal, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}`,
    },
    {
      name: 'Tổng tiền',
      origin:
        `${roundTo(materialLaborOriginTotal, 0).toLocaleString('vi-VN')}` +
        ` + ${roundTo(extendedOriginTotal, 0).toLocaleString('vi-VN')}` +
        ` = ${roundTo(materialLaborOriginTotal + extendedOriginTotal, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}`,
      after:
        `${roundTo(totalMaterialAndLabor, 0).toLocaleString('vi-VN')}` +
        ` + ${roundTo(extendedOriginTotal, 0).toLocaleString('vi-VN')}` +
        ` = ${roundTo(totalPrice, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}`,
      note: discountedPlankPrice
        ? `${roundTo(totalPrice, 0).toLocaleString('vi-VN')}` +
          ` - 
              ${roundTo(discountedPlankPrice, 0)?.toLocaleString('vi-VN')}
            ` +
          ` = ${roundTo(totalPrice - discountedPlankPrice, 0)?.toLocaleString('vi-VN', { currency: 'VND', style: 'currency' })}`
        : '',
    },
  ];
  // container.innerHTML = `
  //       <div class="flex p-24 flex-col gap-24">
  //       ${[...materialAndLaborPrices, ...extendedPrices]
  //         .map(({ name, total, formular }) => {
  //           if (!total) return;
  //           const list =
  //             formular
  //               ?.map(item => {
  //                 if (!item.param1 || !item.param2?.price) return;
  //                 return `<div class="text-body-small">${
  //                   item.note ? `(${item.note})` : ''
  //                 }<span class="text-label-large">${roundTo(item.param1, 3)} ${
  //                   item.param2?.unit ?? ''
  //                 }  *  ${roundTo(item.param2?.price, 0).toLocaleString(
  //                   'vi-VN'
  //                 )} </span> đ<span class="text-label-large"> =  ${roundTo(
  //                   item.param2?.price * item.param1,
  //                   0
  //                 ).toLocaleString('vi-VN')}</span> đ</div>`;
  //               })
  //               ?.filter(item => item)
  //               ?.join(' + ') +
  //             ` = ${roundTo(
  //               formular?.reduce((a, b) => {
  //                 return a + b.param2?.price * b.param1;
  //               }, 0),
  //               0
  //             ).toLocaleString('vi-VN')} đ`;
  //           return render(
  //             name,
  //             total ? `${roundTo(total, 0)?.toLocaleString('vi-VN')} đ.` : null,
  //             `${list}`
  //           );
  //         })
  //         .join('')}

  //       ${(() => {
  //         let priceOrigin = [];
  //         let priceAfter = [];

  //         materialAndLaborPrices?.forEach(item => {
  //           const { name, total, formular } = item;
  //           const origin = formular?.reduce((prev, curr) => {
  //             return prev + roundTo(curr.param2?.price * curr.param1, 0);
  //           }, 0);
  //           if (!origin) return;
  //           priceOrigin.push({ name, origin });
  //           priceAfter.push({ name, after: total });
  //         });

  //         const totalOrigin = priceOrigin.reduce(
  //           (prev, curr) => prev + curr.origin,
  //           0
  //         );
  //         const totalAfter = priceAfter.reduce(
  //           (prev, curr) => prev + curr.after,
  //           0
  //         );

  //         return `
  //         <div class="flex flex-col gap-[8px] items-start justify-start">
  //           <span class="title-large !font-bold">Giá vốn vật liệu & nhân công:</span>
  //           <div class="flex flex-wrap gap-[8px] items-start justify-start">
  //             ${priceOrigin
  //               .map(
  //                 item => `
  //               <div class="flex gap-[4px] items-center justify-start">
  //                 <span class="text-body-medium" title="${item.name}">${roundTo(
  //                   item.origin,
  //                   0
  //                 ).toLocaleString('vi-VN')}</span>
  //               </div>
  //             `
  //               )
  //               .join('+')}
  //               = <span class="text-body-medium !font-bold" title="Tổng giá vốn">${roundTo(
  //                 totalOrigin,
  //                 0
  //               ).toLocaleString('vi-VN')} đ</span>
  //           </div>
  //         </div>
  //         <div class="flex flex-col gap-[8px] items-start justify-start">
  //           <span class="title-large !font-bold">Giá vật liệu & nhân công sau TSLN (Vốn/${roundTo(profitMargin * 100, 0)}%) * 1.08: (${roundTo(totalOrigin, 0).toLocaleString('vi-VN')}/${profitMargin}) * 1.08 = ${roundTo(totalMaterialAndLabor).toLocaleString('vi-VN')} đ</span>
  //           <div class="flex flex-wrap gap-[8px] items-start justify-start">
  //             ${priceAfter
  //               .map(
  //                 item => `
  //               <div class="flex gap-[4px] items-center justify-start">
  //                 <span class="text-body-medium" title="${item.name}">${roundTo(
  //                   item.after,
  //                   0
  //                 ).toLocaleString('vi-VN')}</span>
  //               </div>
  //             `
  //               )
  //               .join('+')}
  //               = <span class="text-body-medium !font-bold" title="Tổng giá bán">${roundTo(
  //                 priceAfter.reduce((prev, curr) => prev + curr.after, 0),
  //                 0
  //               ).toLocaleString('vi-VN')} đ</span>
  //           </div>
  //         </div>
  //       `;
  //       })()}

  //       ${(() => {
  //         let priceOrigin = [];
  //         let priceAfter = [];

  //         extendedPrices?.forEach(item => {
  //           const { name, total, formular } = item;
  //           const origin = formular?.reduce((prev, curr) => {
  //             return prev + roundTo(curr.param2?.price * curr.param1, 0);
  //           }, 0);
  //           if (!origin) return;
  //           priceOrigin.push({ name, origin });
  //           priceAfter.push({ name, after: total });
  //         });

  //         const totalOrigin = priceOrigin.reduce(
  //           (prev, curr) => prev + curr.origin,
  //           0
  //         );
  //         const totalAfter = priceAfter.reduce(
  //           (prev, curr) => prev + curr.after,
  //           0
  //         );

  //         return `
  //         <div class="flex flex-col gap-[8px] items-start justify-start">
  //           <span class="title-large !font-bold">Tổng giá phụ kiện đơn, khối bo cong và mặt bếp, vách bếp:</span>
  //           <div class="flex flex-wrap gap-[8px] items-start justify-start">
  //             ${priceOrigin
  //               .map(
  //                 item => `
  //               <div class="flex gap-[4px] items-center justify-start">
  //                 <span class="text-body-medium" title="${item.name}">${roundTo(
  //                   item.origin,
  //                   0
  //                 ).toLocaleString('vi-VN')}</span>
  //               </div>
  //             `
  //               )
  //               .join('+')}
  //               = <span class="text-body-medium !font-bold" title="Tổng giá vốn">${roundTo(
  //                 totalOrigin,
  //                 0
  //               ).toLocaleString('vi-VN')} đ</span>
  //           </div>
  //         </div>
  //       `;
  //       })()}
  //       <div class="flex flex-col gap-[8px] items-start justify-start">
  //           <span class="title-large !font-bold">Tổng giá bán = GIÁ PHỤ KIỆN ĐƠN + GIÁ KHUNG GỖ ÁP DỤNG TSLN:</span>
  //           <div class="flex flex-wrap gap-[8px] items-start justify-start">
  //             <div class="flex gap-[4px] items-center justify-start">
  //                 <span class="text-body-medium" title="Tổng giá phụ kiện đơn, khối bo cong và mặt bếp, vách bếp">${roundTo(totalOthers.total, 0).toLocaleString('vi-VN')}</span>
  //                 +
  //                 <span class="text-body-medium" title="Giá vật liệu & nhân công sau TSLN">${roundTo(totalMaterialAndLabor, 0).toLocaleString('vi-VN')}</span>
  //                 =
  //                 <span class="text-body-medium !font-bold" title="Giá vật liệu & nhân công sau TSLN">${roundTo(totalPrice, 0).toLocaleString('vi-VN')}</span>
  //               </div>
  //           </div>
  //         </div>
  //       </div>
  //   `;
  // ${render(
  //     'Tổng giá khung gỗ áp dụng TSLN: ((NGUYÊN VẬT LIỆU + NHÂN CÔNG + VẬN CHUYỂN)/TỶ SUẤT LỢI NHẬN)*1.08',
  //     null,
  //     `(${roundTo(totalMaterialAndLabor, 0).toLocaleString(
  //       'vi-VN'
  //     )} ÷ ${roundTo(profitMargin * 100, 0)}%)*1.08 = ${roundTo(
  //       totalFrames,
  //       0
  //     ).toLocaleString('vi-VN')} đ`
  //   )}
}

/**
 * Hàm chính để vẽ visualization
 * @param {Object} packingResult - Kết quả từ getPackingResult()
 * @param {string} containerId - ID của container để vẽ
 * @param {Object} options - Tùy chọn vẽ
 */
function visualizePackingResult(
  packingResult,
  containerId,
  options = {},
  title
) {
  const opts = {
    scale: options.scale || 1,
    maxCanvasWidth: options.maxCanvasWidth || 350,
    maxCanvasHeight: options.maxCanvasHeight || 350,
    showLabels: options.showLabels !== false,
    showStats: options.showStats !== false,
    showLegend: options.showLegend !== false,
    showLengthDirection: options.showLengthDirection !== false, // Mặc định hiển thị
    colorMode: options.colorMode || 'auto',
    containerClass: options.containerClass || 'packing-visualizer',
  };

  const containerAll = document.getElementById(containerId);
  if (!containerAll) {
    console.error(`Container with id "${containerId}" not found`);
    return null;
  }

  const container = document.createElement('div');
  containerAll.appendChild(container);

  container.innerHTML = `
    <div class="${opts.containerClass}">
      <div class="viz-header">
        <h3>📦 Ván ${title}</h3>
        <div class="viz-stats"></div>
      </div>
      <div class="viz-controls">
        <label>Scale: <input type="range" class="scale-slider" min="0.5" max="2" step="0.1" value="${opts.scale}"> <span class="scale-value">${opts.scale}x</span></label>
      </div>
      <div class="viz-bins-container"></div>
    </div>
  `;

  // Thêm CSS
  addVisualizationCSS();

  // Vẽ bins
  drawBins(container, packingResult, opts);

  // Setup controls
  setupVisualizationControls(container, packingResult, opts);

  return {
    container: container,
    refresh: () => drawBins(container, packingResult, opts),
    export: () => exportVisualization(containerId),
  };
}

/**
 * Thêm CSS cho visualization
 */
function addVisualizationCSS() {
  if (document.getElementById('packing-viz-css')) return;

  const css = `
    <style id="packing-viz-css">
    .packing-visualizer {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      max-width: 100%;
      margin: 20px 0;
    }
    .viz-header {
      text-align: center;
      margin-bottom: 20px;
      padding: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-radius: 10px;
    }
    .viz-header h3 {
      margin: 0 0 15px 0;
      font-size: 1.5em;
    }
    .viz-stats {
      display: flex;
      justify-content: center;
      gap: 20px;
      flex-wrap: wrap;
    }
    .viz-stat-item {
      background: rgba(255,255,255,0.2);
      padding: 8px 15px;
      border-radius: 20px;
      font-size: 0.9em;
    }
    .viz-controls {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 20px;
      margin: 20px 0;
      padding: 15px;
      background: #f8f9fa;
      border-radius: 8px;
    }
    .viz-controls label {
      display: flex;
      align-items: center;
      gap: 10px;
      font-weight: 500;
    }
    .scale-slider {
      width: 120px;
    }
    .export-btn {
      background: linear-gradient(135deg, #00b894, #00a085);
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 5px;
      cursor: pointer;
      font-size: 14px;
    }
    .export-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    }
    .viz-bins-container {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
      gap: 20px;
      margin-top: 20px;
    }
    .viz-bin {
      background: white;
      border-radius: 10px;
      padding: 15px;
      box-shadow: 0 4px 15px rgba(0,0,0,0.1);
      border: 1px solid #e9ecef;
      max-width: 500px,
    }
    .viz-bin-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
      padding-bottom: 10px;
      border-bottom: 1px solid #eee;
    }
    .viz-bin-title {
      font-weight: 600;
      color: #2c3e50;
    }
    .viz-bin-efficiency {
      background: linear-gradient(135deg, #74b9ff, #0984e3);
      color: white;
      padding: 4px 10px;
      border-radius: 15px;
      font-size: 0.85em;
    }
    .viz-bin-canvas {
      display: flex;
      justify-content: center;
      margin: 15px 0;
    }
    .viz-canvas {
      border: 2px solid #2c3e50;
      border-radius: 5px;
      background: #f8f9fa;
    }
    .viz-legend {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 15px;
      justify-content: center;
    }
    .viz-legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
      background: #f8f9fa;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 0.8em;
      border: 1px solid #dee2e6;
    }
    .viz-legend-color {
      width: 12px;
      height: 12px;
      border-radius: 2px;
      border: 1px solid #999;
    }
    @media (max-width: 768px) {
      .viz-bins-container {
        grid-template-columns: 1fr;
      }
      .viz-controls {
        flex-direction: column;
        gap: 10px;
      }
    }
    </style>
  `;
  document.head.insertAdjacentHTML('beforeend', css);
}

/**
 * Vẽ các bins
 */
function drawBins(container, packingResult, options) {
  const binsContainer = container.querySelector('.viz-bins-container');

  if (!packingResult.bins || packingResult.bins.length === 0) {
    binsContainer.innerHTML =
      '<div style="text-align: center; color: #6c757d; padding: 40px;">No bins to display</div>';
    return;
  }

  binsContainer.innerHTML = '';

  packingResult.bins.forEach((bin, index) => {
    const binElement = createBinElement(bin, packingResult.binSize, options);
    binsContainer.appendChild(binElement);
  });
}

/**
 * Tạo element cho một bin
 */
function createBinElement(bin, binSize, options) {
  const binDiv = document.createElement('div');
  binDiv.className = 'viz-bin';

  // Tính toán kích thước canvas
  const maxWidth = options.maxCanvasWidth;
  const maxHeight = options.maxCanvasHeight;
  const scaleX = maxWidth / binSize.width;
  const scaleY = maxHeight / binSize.height;
  const scale = Math.min(scaleX, scaleY) * options.scale;

  const canvasWidth = binSize.width * scale;
  const canvasHeight = binSize.height * scale;

  binDiv.innerHTML = `
    <div class="viz-bin-header">
      <div class="viz-bin-title">Bin ${bin.index + 1} (${binSize.width} × ${
        binSize.height
      })</div>
      <div class="viz-bin-efficiency">${bin.efficiency}% filled</div>
    </div>
    <div class="viz-bin-canvas">
      <canvas class="viz-canvas" width="${canvasWidth}" height="${canvasHeight}"></canvas>
    </div>
    <div class="viz-legend"></div>
  `;

  // Vẽ lên canvas
  const canvas = binDiv.querySelector('.viz-canvas');
  drawBinCanvas(canvas, bin, binSize, scale, options);

  // Tạo legend
  if (options.showLegend) {
    const legend = binDiv.querySelector('.viz-legend');
    createLegend(legend, bin.items);
  }

  return binDiv;
}

/**
 * Vẽ bin lên canvas
 */
function drawBinCanvas(canvas, bin, binSize, scale, options) {
  const ctx = canvas.getContext('2d');

  // Clear canvas
  ctx.fillStyle = '#f8f9fa';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Vẽ items
  bin.items.forEach((item, index) => {
    drawItem(ctx, item, index, scale, options);
  });
}

/**
 * Vẽ một item lên canvas
 */
function drawItem(ctx, item, itemIndex, scale, options) {
  const x = item.x * scale;
  const y = item.y * scale;
  const width = item.width * scale;
  const height = item.height * scale;

  // Lấy màu cho item
  const color = getItemColor(item, itemIndex, options.colorMode);

  // Vẽ fill
  ctx.fillStyle = color;
  ctx.fillRect(x, y, width, height);

  // Vẽ border
  ctx.strokeStyle = '#2c3e50';
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, width, height);

  // Vẽ length direction indicator
  if (options.showLengthDirection !== false) {
    drawLengthIndicator(ctx, item, x, y, width, height);
  }

  // Vẽ label nếu được yêu cầu
  if (options.showLabels && width > 30 && height > 20) {
    drawItemLabel(ctx, item, x, y, width, height);
  }

  // Vẽ rotation indicator
  if (item.rotated) {
    drawRotationIndicator(ctx, x, y, width, height);
  }
}

/**
 * Vẽ label cho item
 */
function drawItemLabel(ctx, item, x, y, width, height) {
  ctx.fillStyle = '#2c3e50';
  ctx.font = `${Math.max(10, Math.min(width / 8, height / 4, 14))}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const centerX = x + width / 2;
  const centerY = y + height / 2;

  // Vẽ ID hoặc tên
  const label = item.name || item.id || item.sku;
  const maxLength = Math.floor(width / 8);
  const displayLabel =
    label.length > maxLength ? label.substring(0, maxLength) + '...' : label;

  ctx.fillText(displayLabel, centerX, centerY - 5);

  // Vẽ kích thước
  const sizeLabel = `${roundTo(item.width, 3)}×${roundTo(item.height, 3)}`;
  if (height > 35) {
    ctx.font = `${Math.max(8, Math.min(width / 10, height / 6, 12))}px Arial`;
    ctx.fillText(sizeLabel, centerX, centerY + 10);
  }
}

/**
 * Vẽ indicator cho items đã xoay
 */
function drawRotationIndicator(ctx, x, y, width, height) {
  ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';
  ctx.fillRect(x + width - 12, y + 2, 10, 10);

  ctx.fillStyle = 'white';
  ctx.font = '8px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('R', x + width - 7, y + 8);
}

/**
 * Lấy màu cho item
 */
function getItemColor(item, itemIndex, colorMode) {
  switch (colorMode) {
    case 'byId':
      const idHash = item.id ? hashCode(item.id.toString()) : itemIndex;
      return VISUALIZATION_COLORS[
        Math.abs(idHash) % VISUALIZATION_COLORS.length
      ];

    case 'byType':
      const type = item.type || item.category || 'default';
      const typeHash = hashCode(type.toString());
      return VISUALIZATION_COLORS[
        Math.abs(typeHash) % VISUALIZATION_COLORS.length
      ];

    default: // 'auto'
      return VISUALIZATION_COLORS[itemIndex % VISUALIZATION_COLORS.length];
  }
}

/**
 * Hash function cho string
 */
function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash;
}

/**
 * Tạo legend
 */
function createLegend(legendContainer, items) {
  const uniqueItems = {};
  items.forEach((item, index) => {
    const key = item.id || item.name || item.sku || `Item ${index + 1}`;
    if (!uniqueItems[key]) {
      uniqueItems[key] = {
        item: item,
        color: VISUALIZATION_COLORS[index % VISUALIZATION_COLORS.length],
        count: 1,
        rotated: item.rotated || false,
      };
    } else {
      uniqueItems[key].count++;
      if (item.rotated) uniqueItems[key].rotated = true;
    }
  });

  legendContainer.innerHTML = '';
  Object.entries(uniqueItems).forEach(([_, data]) => {
    const legendItem = document.createElement('div');
    legendItem.className = 'viz-legend-item';

    const colorBox = document.createElement('div');
    colorBox.className = 'viz-legend-color';
    colorBox.style.backgroundColor = data.color;

    const label = document.createElement('span');
    const displayLabel = data.item.name;

    // Thêm length direction indicator
    const lengthDir = data.item.width >= data.item.height ? '↔️' : '↕️';

    // Thêm warning icons
    let warnings = '';
    if (data.rotated) warnings += ' 🔄';

    label.innerHTML = `${displayLabel} (${roundTo(
      data.item.width,
      3
    )}×${roundTo(data.item.height, 3)}) ${lengthDir}${warnings}${
      data.count > 1 ? ` ×${data.count}` : ''
    }`;

    legendItem.appendChild(colorBox);
    legendItem.appendChild(label);
    legendContainer.appendChild(legendItem);
  });
}

/**
 * Setup controls cho visualization
 */
function setupVisualizationControls(container, packingResult, options) {
  const scaleSlider = container.querySelector('.scale-slider');
  const scaleValue = container.querySelector('.scale-value');

  if (scaleSlider) {
    scaleSlider.oninput = function () {
      options.scale = parseFloat(this.value);
      scaleValue.textContent = options.scale + 'x';
      drawBins(container, packingResult, options);
    };
  }
}

/**
 * Export visualization thành PNG
 */
function exportVisualization(containerId) {
  const container = document.getElementById(containerId);
  if (!container) return;

  const canvases = container.querySelectorAll('.viz-canvas');
  if (canvases.length === 0) return;

  // Tạo canvas tổng hợp
  const exportCanvas = document.createElement('canvas');
  const ctx = exportCanvas.getContext('2d');

  // Tính toán layout
  const binsPerRow = Math.ceil(Math.sqrt(canvases.length));
  const maxCanvasWidth = Math.max(...Array.from(canvases).map(c => c.width));
  const maxCanvasHeight = Math.max(...Array.from(canvases).map(c => c.height));

  const padding = 20;
  const binWidth = maxCanvasWidth + padding;
  const binHeight = maxCanvasHeight + padding * 2; // thêm chỗ cho title

  exportCanvas.width = binsPerRow * binWidth;
  exportCanvas.height = Math.ceil(canvases.length / binsPerRow) * binHeight;

  // Background trắng
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

  // Vẽ từng bin
  canvases.forEach((canvas, index) => {
    const row = Math.floor(index / binsPerRow);
    const col = index % binsPerRow;
    const x = col * binWidth + padding / 2;
    const y = row * binHeight + padding;

    // Vẽ title
    const binElement = canvas.closest('.viz-bin');
    const title = binElement.querySelector('.viz-bin-title').textContent;
    const efficiency = binElement.querySelector(
      '.viz-bin-efficiency'
    ).textContent;

    ctx.fillStyle = '#2c3e50';
    ctx.font = '16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`${title} - ${efficiency}`, x + canvas.width / 2, y - 5);

    // Vẽ canvas
    ctx.drawImage(canvas, x, y);
  });

  // Download
  const link = document.createElement('a');
  link.download = 'bin-packing-visualization.png';
  link.href = exportCanvas.toDataURL();
  link.click();
}
export {
  clearVisual,
  visualizeTotal,
  visualizePackingResult,
  exportVisualization,
  MaxRectsPacker,
  NestingAlgorithm,
  ItemRetangle,
};
